import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, ImagePlus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { getEvents, createEvent, updateEvent, deleteEvent, uploadFile } from '../../services/api';
import { toast } from 'sonner';

const EventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', location: '' });
  const [eventImages, setEventImages] = useState([]);

  const fetchData = async () => {
    try {
      const res = await getEvents();
      setEvents(res.data.events);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', description: '', event_date: '', location: '' });
    setEventImages([]);
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setForm({ title: event.title, description: event.description || '', event_date: event.event_date || '', location: event.location || '' });
    setEventImages([]);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateEvent(editing.id, { ...editing, ...form });
        toast.success('Event updated');
      } else {
        const res = await createEvent(form);
        // Upload images if any
        if (eventImages.length > 0 && res.data?.id) {
          const uploadedImages = [];
          for (const file of eventImages) {
            const uploadRes = await uploadFile(file, 'event');
            uploadedImages.push({ url: uploadRes.data.url, caption: file.name });
          }
          await updateEvent(res.data.id, { ...res.data, images: uploadedImages });
        }
        toast.success('Event created');
      }
      setDialogOpen(false);
      setEventImages([]);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await deleteEvent(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const handleImageUpload = async (eventId, file) => {
    try {
      const res = await uploadFile(file, 'event');
      const event = events.find(e => e.id === eventId);
      const images = [...(event.images || []), { url: res.data.url, caption: file.name }];
      await updateEvent(eventId, { ...event, images });
      toast.success('Image added');
      fetchData();
    } catch { toast.error('Upload failed'); }
  };

  return (
    <div className="space-y-6" data-testid="events-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500">{events.length} events</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-event-btn">
          <Plus size={16} className="mr-2" /> Add Event
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-24" /></Card>)}</div>
      ) : events.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No events yet</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.id} className="hover:shadow-sm transition-shadow" data-testid={`event-${event.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{event.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{event.location} {event.event_date && `- ${event.event_date}`}</p>
                    {event.description && <p className="text-sm text-gray-600 mt-2">{event.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(event)}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(event.id)} className="text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                  </div>
                </div>
                {event.images?.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {event.images.map((img, i) => (
                      <img key={i} src={img.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url} alt={img.caption || ''} className="w-20 h-16 object-cover rounded flex-shrink-0" />
                    ))}
                  </div>
                )}
                <label className="cursor-pointer mt-2 inline-flex items-center gap-1 text-xs text-[#2E3A6F] hover:underline">
                  <ImagePlus size={14} /> Add Photos
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleImageUpload(event.id, e.target.files[0])} />
                </label>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Event</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="event-title-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} placeholder="2025-03-15" /></div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <div>
              <Label>Event Images (800x600 px)</Label>
              <Input type="file" accept="image/*" multiple onChange={(e) => setEventImages(Array.from(e.target.files))} className="text-sm" data-testid="event-images-upload" />
              {eventImages.length > 0 && <p className="text-[10px] text-green-600 mt-1">{eventImages.length} file(s) selected</p>}
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="event-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsAdmin;
